import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Dealer, DealerRiskTier } from "@dfp/api-client";
import { Badge, type BadgeTone } from "../components/Badge.js";
import { Card, CardBody, CardHeader, CardTitle } from "../components/Card.js";
import { CreateDealerForm } from "../components/CreateDealerForm.js";
import { Table, TBody, THead, Th, Td, Tr } from "../components/Table.js";
import { api } from "../lib/api.js";

const TIER_TONE: Record<DealerRiskTier, BadgeTone> = {
  A: "success",
  B: "neutral",
  C: "warning",
};

export function DealersPage(): JSX.Element {
  const dealers = useQuery({
    queryKey: ["dealers", "list"],
    queryFn: () => api.get<readonly Dealer[]>("/api/dealers"),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Dealers</h1>
        <p className="text-sm text-slate-500">Authorised resale partners.</p>
      </div>

      <CreateDealerForm />

      <Card>
        <CardHeader>
          <CardTitle>All dealers</CardTitle>
          <span className="text-xs text-slate-500">{dealers.data?.length ?? 0} rows</span>
        </CardHeader>
        <CardBody className="p-0">
          {dealers.isLoading ? (
            <p className="p-5 text-sm text-slate-500">Loading…</p>
          ) : dealers.isError ? (
            <p className="p-5 text-sm text-red-700">Failed to load dealers.</p>
          ) : (dealers.data ?? []).length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No dealers yet.</p>
          ) : (
            <Table>
              <THead>
                <tr>
                  <Th>ID</Th>
                  <Th>Name</Th>
                  <Th>Risk tier</Th>
                  <Th>Stock limit</Th>
                  <Th>Status</Th>
                  <Th>Created</Th>
                </tr>
              </THead>
              <TBody>
                {dealers.data?.map((d) => (
                  <Tr key={d.id}>
                    <Td>
                      <Link
                        to="/dealers/$id"
                        params={{ id: d.id }}
                        className="font-mono text-xs text-brand-600 hover:underline"
                      >
                        {d.id.slice(0, 8)}…
                      </Link>
                    </Td>
                    <Td>{d.name}</Td>
                    <Td>
                      <Badge tone={TIER_TONE[d.riskTier]}>{d.riskTier}</Badge>
                    </Td>
                    <Td>{d.stockLimit}</Td>
                    <Td>
                      {d.suspendedAt ? (
                        <Badge tone="danger">suspended</Badge>
                      ) : (
                        <Badge tone="success">active</Badge>
                      )}
                    </Td>
                    <Td className="text-xs text-slate-500">
                      {new Date(d.createdAt).toLocaleDateString()}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
